/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ReactNode } from 'react';
import { forwardRef, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ChatResponseField, type ChatResponseFieldRef } from '@coursera-web/cds-labs';
import classNames from 'classnames';

import { useTracker } from '@coursera/event-pulse/react';

import RichInputField, {
  type Props as RichInputFieldProps,
  type RichInputFieldRef,
} from 'bundles/ai-coach-platform/components/patterns/chat/common/RichInputField';
import type { SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import SuggestedActionsPopover from 'bundles/ai-coach-platform/components/patterns/popover/SuggestedActionsPopover';
import { CoachAction } from 'bundles/ai-coach-platform/constants';
import { useCdsLabsChatComponents } from 'bundles/ai-coach-platform/utils/statsigFeatureFlags';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import type { GlobalState } from '../store/types';

type Props = {
  placeholder: string;
  disableInput: boolean;
  disableSpeechToText?: boolean;
  speechToTextDisabledTooltip?: string;
  sendMessage: SendMessageFunc;
  /**
   * Which input UI to render. `'rich'` (default) keeps the existing ai-coach-platform
   * RichInputField. `'cds-labs'` renders the cds-labs ChatResponseField instead, while this
   * wrapper continues to own the Redux store wiring and send eventing.
   *
   * NOTE: This prop is temporary. It lets consumers opt into the new GenAI components
   * incrementally; once the platform is updated to use them everywhere, the `'rich'` path and
   * this prop should be removed.
   */
  inputVariant?: 'rich' | 'cds-labs';
  /**
   * Custom attachments rendered in the composer's context row (above the text input).
   * Only applies to the `'cds-labs'` variant, which forwards them to ChatResponseField.
   */
  contextAttachments?: ReactNode;
} & Omit<RichInputFieldProps, 'placeholder'>;

const styles = {
  container: css`
    padding: 0 var(--cds-spacing-200);

    .coach-chat-input-field {
      padding: var(--cds-spacing-200) 0 0 0;
    }
  `,
};

/**
 * Wrapper around `RichInputField` that handles the Redux store interaction and eventing for Coach chats
 */
const ChatRichInputField = forwardRef<RichInputFieldRef, Props>((props, richInputRef) => {
  const {
    sendMessage,
    placeholder,
    disableInput,
    disableSpeechToText,
    speechToTextDisabledTooltip,
    allowSendWithEmptyMessage,
    inputVariant = 'rich',
    onTextInputChange,
    contextAttachments,
    ...richInputFieldProps
  } = props;
  const isCdsLabsEnabled = useCdsLabsChatComponents();
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));

  const refContainer = useRef<HTMLDivElement>(null);
  const responseFieldRef = useRef<ChatResponseFieldRef>(null);
  const [inputText, setInputText] = useState('');
  const [listening, setListening] = useState(false);
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const handleSendMessageClick = (message: string) => {
    // ignore empty messages unless the parent has external content to commit
    if (!message && !allowSendWithEmptyMessage) {
      return;
    }

    sendMessage(message, CoachAction.TypedMessage);

    if (trackingContext) {
      // v3 tracking
      track('interact_coach', {
        coachInteractionType: 'send_message',
        coach: {
          mode: trackingContext.mode,
          location: 'chat',
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
            type: trackingContext.itemType,
          },
        }),
        ...(trackingContext.form && { form: trackingContext.form }),
      });

      // This allows us to send the input message to amplitude as a separate event all together.
      // The main goal for now being to derive the character count of the input text as requested by product.
      track('send_coach_message', {
        coachMessageText: message,
        coachMessageLength: message.length,
        coach: {
          mode: trackingContext.mode,
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
            type: trackingContext.itemType,
          },
        }),
        ...(trackingContext.form && { form: trackingContext.form }),
      });
    }
  };

  if (inputVariant === 'cds-labs' || isCdsLabsEnabled) {
    // The cds-labs ChatResponseField is presentational: this wrapper keeps owning the
    // Redux-driven disabled/streaming state and the send eventing (handleSendMessageClick).
    // ChatResponseField does not clear itself on send in uncontrolled mode, so we clear it
    // via its ref after each send. Note: it has no speech-to-text and no stop button (no
    // platform stop action).
    const isDisabled = isMessageLoading || isMessageStreaming || disableInput;

    const handleResponseFieldChange = (value: string) => {
      setInputText(value);
      onTextInputChange?.(value);
    };

    const handleResponseFieldSend = (sendValue: string) => {
      handleSendMessageClick(sendValue);
      setInputText('');
      responseFieldRef.current?.clear();
    };

    return (
      <div ref={refContainer} css={styles.container}>
        <div
          className={classNames('coach-chat-input-field', {
            'coach-message-disabled': isMessageLoading || disableInput,
          })}
        >
          <ChatResponseField
            ref={responseFieldRef}
            placeholder={placeholder}
            onSend={handleResponseFieldSend}
            onChange={handleResponseFieldChange}
            onMicrophoneStart={() => setListening(true)}
            onMicrophoneStop={() => setListening(false)}
            contextAttachments={contextAttachments}
            disabled={isDisabled}
            allowEmptySend={allowSendWithEmptyMessage}
            autoFocus={disableInput ? false : richInputFieldProps.autofocus}
            maxLength={richInputFieldProps.maxLength}
            data-testid="coach-chat-rich-input-field"
            aria-label={richInputFieldProps['aria-label']}
            value={richInputFieldProps.value}
            disableSpeechToText={disableSpeechToText}
            speechToTextDisabledTooltip={speechToTextDisabledTooltip}
            leadingAction={richInputFieldProps.actionButtons}
            trailingActions={
              richInputFieldProps.suggestedActions ? (
                <SuggestedActionsPopover
                  actions={richInputFieldProps.suggestedActions}
                  disabled={isDisabled || inputText.length > 0 || listening}
                />
              ) : undefined
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={refContainer} css={styles.container}>
      <div
        className={classNames('coach-chat-input-field', {
          'coach-message-disabled': isMessageLoading || disableInput,
        })}
      >
        <RichInputField
          ref={richInputRef}
          disableInput={disableInput}
          disableSpeechToText={disableSpeechToText}
          speechToTextDisabledTooltip={speechToTextDisabledTooltip}
          disabled={isMessageLoading || isMessageStreaming}
          placeholder={placeholder}
          data-testid="coach-chat-rich-input-field"
          onSend={handleSendMessageClick}
          allowSendWithEmptyMessage={allowSendWithEmptyMessage}
          {...richInputFieldProps}
        />
      </div>
    </div>
  );
});

export default ChatRichInputField;
